import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box, CircularProgress, Dialog,
  Stack
} from '@mui/material';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useGetCO2eEmissionDataQuery, useGetCO2eEmissionTimestampSummaryQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';

import TollRoundedIcon from '@mui/icons-material/TollRounded';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';
import { DateTime } from 'luxon';
import { usePutEnergyGroupOptionsMutation } from '../../../../api/recalcV2/endpoints/energyGroupsApiSlice';

const KpiCard = React.lazy(() =>
  import('remote/KpiCard')
    .catch(() => ({
      default: () => <div>Failed to load</div>,
    }))
);

const DeclarationCard = React.lazy(() =>
  import('remote/DeclarationCard')
    .catch(() => ({
      default: () => <div>Failed to load</div>,
    }))
);

const GoCard = React.lazy(() =>
  import('remote/GoCard')
    .catch(() => ({
      default: () => <div>Failed to load</div>,
    }))
);

const EsgDistributionPieChart = React.lazy(() =>
  import('remote/EsgDistributionPieChart')
    .catch(() => ({
      default: () => <div>Failed to load</div>,
    }))
);

const EsgLineChart = React.lazy(() =>
  import('remote/EsgLineChart')
    .catch(() => ({
      default: () => <div>Failed to load</div>,
    }))
);

const GoSharesDatagrid = React.lazy(() =>
  import('remote/GoSharesDatagrid')
    .catch(() => ({
      default: () => <div>Failed to load</div>,
    }))
);

const SeuDatagrid = React.lazy(() =>
  import('remote/SeuDatagrid')
    .catch(() => ({
      default: () => <div>Failed to load</div>,
    }))
);

const YearOnYearGraph = React.lazy(() => import('remote/YearOnYearGraph').catch(() => ({ default: () => <div>Failed to load</div> })));

const formatNumber = (number) => {
  return new Intl.NumberFormat('da-DK').format(number);
};
const EsgOverviewTab = (props) => {
  const {
    selectedEnergyGroup,
    selectedAccount,
    timeRange,
    setTimestampSummary,
    setTimeRange
  } = props;

  const dispatch = useDispatch()

  const { data: timestampsSummary, isSuccess: isTimestampsSummarySuccess, isFetching: isTimestampsSummaryFetching } = useGetCO2eEmissionTimestampSummaryQuery({
    accountId: selectedAccount?.AccountId,
    energyGroupId: selectedEnergyGroup?.energyGroupId,
    timerange: timeRange
  }, {
    skip: !selectedAccount?.AccountId || selectedEnergyGroup === null
  })

  const isTimestampCorrected = useMemo(() => {
    if (!isTimestampsSummarySuccess || !timestampsSummary) return false;

    const maxEarliest = DateTime.fromISO(timestampsSummary.maxEarliestDataTimestamp || '');
    const minLatest = DateTime.fromISO(timestampsSummary.minLatestDataTimestamp || '');

    const currentStart = DateTime.fromMillis(timeRange.start);
    const currentEnd = DateTime.fromMillis(timeRange.end);

    return (currentStart >= maxEarliest && currentEnd <= minLatest);
  }, [timestampsSummary, timeRange]);
  const {
    data: esgOverviewData,
    isLoading: isEsgOverviewDataLoading,
    isError: isEsgOverviewDataError,
    isFetching: isEsgOverviewDataFetching,
    isSuccess: isEsgOverviewDataSuccess,
  } = useGetCO2eEmissionDataQuery({
    accountId: selectedAccount?.AccountId,
    energyGroupId: selectedEnergyGroup?.energyGroupId,
    start: timeRange?.start,
    end: timeRange?.end
  }, { skip: !selectedAccount?.AccountId || selectedEnergyGroup === null || timeRange.start === null || timeRange.end === null || !isTimestampCorrected });

  useEffect(() => {
    if (isEsgOverviewDataError) {
      dispatch(showSnackbar({ message: 'Der skete en fejl i udregningen af analysen', severity: 'error' }));
    }
  }, [isEsgOverviewDataError])

  const modes = [
    {
      id: 0, label: 'Lokationsbaseret', icon: <PlaceRoundedIcon />, description: 'Dette tal viser CO₂e-udledningen baseret på den fysiske produktion og transport i elnettet, time for time og geografisk område. Dataene afspejler de faktiske forhold og ændrer sig med tid og sted. Data følger Energinets lokationsbaserede miljødeklaration.',
      energySummary: esgOverviewData?.Location?.CO2eEmissionsSummary,
      EnergyTooltip: <Box>
        Den viste CO2e-udledning dækker Scope 2 ifølge GHG-protokollen (Greenhouse Gas Protocol), dvs. emissioner fra forbrugt el, ekskl. tab i elnettet.<br/><br/>
        Transmission og distributionstab er ekskluderet i beregningen. Emmissioner fra tab i elnettet tilhører Scope 3-emissioner, da de sker uden for virksomhedens kontrol, men vises her som reference og til rapportering: <br/><br/>
        • Distributionstab: {formatNumber((esgOverviewData?.Location?.CO2eEmissionsSummary?.DistributionLossEmissions/1000).toFixed(1))} kg CO₂e ({formatNumber(esgOverviewData?.Location?.CO2eEmissionsSummary?.DistributionLossPercentage.toFixed(1))}%)<br/>
        • Transmissionstab: {formatNumber((esgOverviewData?.Location?.CO2eEmissionsSummary?.TransmissionLossEmissions/1000).toFixed(1))} kg CO₂e ({formatNumber(esgOverviewData?.Location?.CO2eEmissionsSummary?.TransmissionLossPercentage.toFixed(1))}%)<br/><br/>
        Beregningerne følger Energistyrelsens lokationsbaserede miljødeklaration.
      </Box>
    },
    {
      id: 1, label: 'Kommunebaseret', icon: <LocationCityRoundedIcon />, description: 'Her vises CO₂e-udledningen for din kommune. Deklarationen tager højde for lokal produktion, forbrug og andelen af grøn energi i området, og er dermed mere præcis for lokale forhold. Data følger Energinets kommunedeklaration.',
      energySummary: esgOverviewData?.Municipality?.CO2eEmissionsSummary,
      ConsumptionTooltip: <Box>
        Det viste elforbrug er korrigerede efter fradrag af lokal vedvarende energi (LokalVE).
        Ifølge Energistyrelsens metode trækkes den andel af forbruget fra, som er dækket af lokalVE-produktion i kommunen. Lokal VE dækker fx solceller, lokale vindmøller eller mindre biogasanlæg i kommunen.
        Dette sikrer, at kun det elforbrug, der belaster det kollektive elnet, indgår i emissionsberegningerne.
      </Box>,
      EnergyTooltip: <Box>
      Den viste CO2e-udledning dækker Scope 2 ifølge GHG-protokollen (Greenhouse Gas Protocol), dvs. emissioner fra forbrugt el, ekskl. tab i elnettet.<br/><br/>
      Transmission og distributionstab er ekskluderet i beregningen. Emmissioner fra tab i elnettet tilhører Scope 3-emissioner, da de sker uden for virksomhedens kontrol, men vises her som reference og til rapportering: <br/><br/>
      • Distributionstab: {formatNumber((esgOverviewData?.Municipality?.CO2eEmissionsSummary?.DistributionLossEmissions/1000).toFixed(1))} kg CO₂e ({formatNumber(esgOverviewData?.Municipality?.CO2eEmissionsSummary?.DistributionLossPercentage.toFixed(1))}%)<br/>
      • Transmissionstab: {formatNumber((esgOverviewData?.Municipality?.CO2eEmissionsSummary?.TransmissionLossEmissions/1000).toFixed(1))} kg CO₂e ({formatNumber(esgOverviewData?.Municipality?.CO2eEmissionsSummary?.TransmissionLossPercentage.toFixed(1))}%)<br/><br/>
      Beregningerne følger Energistyrelsens lokationsbaserede miljødeklaration.
    </Box>
    },
    {
      id: 2, label: 'Markedsbaseret', icon: <TollRoundedIcon />, description: 'Dette tal repræsenterer CO₂e-udledningen baseret på dine købte oprindelsesgarantier (f.eks. grøn strøm) og elhandlerens deklaration. Det afspejler dine valg på elmarkedet - ikke nødvendigvis den faktiske strøm i stikkontakten.',
      energySummary: esgOverviewData?.Market?.CO2eEmissionsSummary,
    },
  ]

  const getGridMixSummary = () => {
    switch (selectedMode) {
      case 0:
        return esgOverviewData?.Location?.GridMixSummary
      case 1:
        return esgOverviewData?.Municipality?.GridMixSummary
      case 2:
        return esgOverviewData?.Market?.GridMixSummary
      default:
        return null;
    }
  }

  const getCO2eData = () => {
    switch (selectedMode) {
      case 0:
        return esgOverviewData?.Location?.CO2eData
      case 1:
        return esgOverviewData?.Municipality?.CO2eData
      case 2:
        return esgOverviewData?.Market?.CO2eData
      default:
        return null;
    }
  }

  const getCO2eEmissions = () => {
    switch (selectedMode) {
      case 0:
        return esgOverviewData?.Location?.CO2eEmissionsSummary?.CO2eEmissions
      case 1:
        return esgOverviewData?.Municipality?.CO2eEmissionsSummary?.CO2eEmissions
      case 2:
        return esgOverviewData?.Market?.CO2eEmissionsSummary?.CO2eEmissions
      default:
        return null;
    }
  }

  const getGridMixData = () => {
    switch (selectedMode) {
      case 0:
        return esgOverviewData?.Location?.GridMixDistributionData
      case 1:
        return esgOverviewData?.Municipality?.GridMixDistributionData
      case 2:
        return esgOverviewData?.Market?.GridMixDistributionData
      default:
        return null;
    }
  }

  const getSeuData = () => {
    switch (selectedMode) {
      case 0:
        return esgOverviewData?.Location?.MonthlyCO2eEmissions
      case 1:
        return esgOverviewData?.Municipality?.MonthlyCO2eEmissions
      case 2:
        return esgOverviewData?.Market?.MonthlyCO2eEmissions
      default:
        return null;
    }
  }

  const [selectedMode, setSelectedMode] = React.useState(0);
  const handleAdjustTimeRange = (tr) => {
    if (!tr) return

    const maxEarliest = DateTime.fromISO(tr.maxEarliestDataTimestamp || '');
    const minLatest = DateTime.fromISO(tr.minLatestDataTimestamp || '');
    const currentStart = DateTime.fromMillis(timeRange.start);
    const currentEnd = DateTime.fromMillis(timeRange.end);

    const isCompletelyOutside =
      currentEnd < maxEarliest || currentStart > minLatest;
  
    const isCompletelyInside = currentStart >= maxEarliest && currentEnd <= minLatest;

    if (isCompletelyInside) return;

    let adjustedStart;
    let adjustedEnd;
  
    if (isCompletelyOutside) {
      // Full fallback: select full available window
      adjustedStart = maxEarliest;
      adjustedEnd = minLatest;
    } else {
      // Normal adjustment: clip only start or end
      adjustedStart = currentStart < maxEarliest ? maxEarliest : currentStart;
      adjustedEnd = currentEnd > minLatest ? minLatest : currentEnd;
  
      // Extra safety: if adjustment somehow causes inverted range
      if (adjustedStart > adjustedEnd) {
        adjustedStart = maxEarliest;
        adjustedEnd = minLatest;
      }
    }
  
    setTimeRange({
      start: adjustedStart.toUTC().toMillis(),
      end: adjustedEnd.toUTC().toMillis(),
    });

    dispatch(showSnackbar({severity: 'info', message: 'Den valgte periode strækker sig uden for perioden hvor vi har data. Intervallet er blevet justeret automatisk.'}));
  };
  useEffect(() => {
    handleAdjustTimeRange(timestampsSummary)
  }, [timestampsSummary])

  const [openGoShares, setOpenGoShares] = useState(false);

  const handleCloseGoShares = () => {
    setOpenGoShares(false);
  };

  const [putEnergyGroupOptions] = usePutEnergyGroupOptionsMutation();

  const handleSaveChanges = async (goShares) => {
    if (!selectedEnergyGroup?.energyGroupId) return;

    const options = selectedEnergyGroup?.options;

    const updates = {
        ...options,
        goShares: goShares,
      }

    try {
      await putEnergyGroupOptions({
        accountId: selectedAccount.AccountId,
        energyGroupId: selectedEnergyGroup.energyGroupId,
        options: updates,
      });
      dispatch(showSnackbar({ message: 'Gruppe opdateret, bemærk at der kan gå op til 24 timer før ændringen tager effekt.', severity: 'success' }));
    } catch (err) {
      console.error('Error updating energy group:', err);
      dispatch(showSnackbar({ message: 'Der opstod en fejl i opdatering af gruppe', severity: 'error' }));
    }
  };

  const reShareCard = [{
    title: 'Den vedvarende energi % andel for perioden',
    description: 'Den vedvarende energi % andel for perioden',
    number: esgOverviewData?.Location?.REShareSummary?.REShare,
    label: `${formatNumber((esgOverviewData?.Location?.REShareSummary?.REShare)?.toFixed(1) || 0)}%`,
  },
  {
    title: 'Den vedvarende energi % andel for perioden',
    description: 'Den vedvarende energi % andel for perioden',
    number: esgOverviewData?.Municipality?.REShareSummary?.REShare,
    label: `${formatNumber((esgOverviewData?.Municipality?.REShareSummary?.REShare)?.toFixed(1) || 0)}%`,
  },
  {
    title: 'Den vedvarende energi % andel for perioden',
    description: 'Den vedvarende energi % andel for perioden',
    number: esgOverviewData?.Market?.REShareSummary?.REShare,
    label: `${formatNumber((esgOverviewData?.Market?.REShareSummary?.REShare)?.toFixed(1) || 0)}%`,
  }]

  if (isEsgOverviewDataFetching ||isTimestampsSummaryFetching) {
    return <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%', // Keeps it within the available space without forcing full height
      }}
    >
      <Card
        sx={{
          width: { xs: '90%', sm: '60%', md: '50%', lg: '40%' }, // Responsive width
          maxWidth: '500px', // Prevents it from getting too wide
          minWidth: '300px', // Ensures a reasonable minimum size
          p: 3,
          borderRadius: 2,
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        variant="outlined"
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'left', mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'JetBrains Mono NL',
                color: (theme) => theme.palette.mode === 'light' ? 'hsl(0, 0%, 20%)' : 'hsl(0, 0%, 85%)',
                fontWeight: 'bold'
              }}
            >
              EnergyXplorer
            </Typography>
          </Box>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
            Beregner CO₂ udledning...
          </Typography>
        </CardContent>
      </Card>
    </Box>
  }

  if (isEsgOverviewDataError) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
      <Card width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }} variant="outlined" sx={{ p: 2, mb: 2, mt: 8, borderRadius: 2 }}>
        <CardContent width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}>
          <Typography variant="h6">Der skete en fejl</Typography>
          <Typography variant="body1" color="text.secondary">CO₂ udledning kunne ikke beregnes. Kontakt os for at få hjælp.</Typography>
        </CardContent>
      </Card>
    </Box>
  }

  return (<>
    {isEsgOverviewDataSuccess &&
    <Box>
      <Grid container spacing={2} paddingTop={0}>
        {modes.map((mode) => (
          <Grid key={mode.id} item md={12} lg={6} xl={3}>
            <Suspense fallback={<div>Loading...</div>}>
              <DeclarationCard key={mode.id} {...mode} timeRange={timeRange} mode={mode.id} setMode={setSelectedMode} selected={mode.id === selectedMode} />
            </Suspense>
          </Grid>))}
        <Grid item md={12} lg={6} xl={3}>
          {selectedMode === 2 ?
            <Suspense fallback={<div>Loading...</div>}>
              <GoCard openGoShares={setOpenGoShares} title={'Andel af GO'} number={esgOverviewData?.Market?.REShareSummary?.REShare} label={'%'} description={'Procentdelen af din strøm som kommer fra vedvarende kilder, baseret på dine køb af oprindelsesgarantier.'} timeRange={timeRange} />
            </Suspense> :
            <Suspense fallback={<div>Loading...</div>}>
              <KpiCard {...reShareCard[selectedMode]} timeRange={timeRange} />
            </Suspense>}
        </Grid>
        {selectedMode !== 2 &&
          <Suspense fallback={<div>Loading...</div>}>
            <EsgDistributionPieChart GridMixData={getGridMixData()} GridMixLocationSummary={getGridMixSummary()} timeRange={timeRange} />
          </Suspense>}

        <Grid item md={12} sx={{ transition: 'flex-basis 0.3s ease' }}>
          


            <Suspense fallback={<div>Loading...</div>}>
              <SeuDatagrid data={getSeuData()} />
            </Suspense>
        </Grid>

        <Grid item md={12} sx={{ transition: 'flex-basis 0.3s ease' }}>

            <Suspense fallback={<div>Loading...</div>}>
              <YearOnYearGraph data={getSeuData()} />
            </Suspense>
        </Grid>

      </Grid>
      <Dialog
        open={openGoShares}
        onClose={handleCloseGoShares}
        maxWidth="lg"
      >
        <Suspense fallback={<div>Loading...</div>}>
          <GoSharesDatagrid handleSaveChanges={handleSaveChanges} initialRows={selectedEnergyGroup?.options?.goShares} />
        </Suspense>
      </Dialog>
    </Box> 
    // : <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
    //   <Card width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }} variant="outlined" sx={{ p: 2, mb: 2, mt: 8, borderRadius: 2 }}>
    //     <CardContent width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}>
    //       <Typography variant="h6">Manglende opsætning</Typography>
    //       <Typography variant="body1" color="text.secondary">Din konto er muligvis ikke sat op til at vise denne side.</Typography>
    //     </CardContent>
    //   </Card>
    // </Box>
    }</>
  );
}

export default EsgOverviewTab